import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { FilesApiService, UploadEvent } from './files-api.service';
import { GradingApiService } from './grading-api.service';
import { GradingStateService } from './grading-state.service';
import { PendingAttachment, Resource } from '../grading.types';
import { AmplitudeService } from '../../../../common/analytics/amplitude.service';

/** Counter for generating local IDs */
let localIdCounter = 0;

/** Example files available in public/images/demo-chat/ */
const EXAMPLE_FILES = [
  '/images/demo-chat/7th Grade General Knowledge Check.pdf',
  '/images/demo-chat/TEACHER ANSWER KEY(7th grade general knowledge check).pdf',
  '/images/demo-chat/Leo_p.jpg',
  '/images/demo-chat/Maya_s.jpg',
  '/images/demo-chat/Sam_t.jpg',
];

@Injectable()
export class FilesService {
  private readonly filesApiService = inject(FilesApiService);
  private readonly gradingApiService = inject(GradingApiService);
  private readonly stateService = inject(GradingStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly amplitudeService = inject(AmplitudeService);

  // Pending attachments (files selected but not yet sent)
  private readonly pendingAttachments$$ = new BehaviorSubject<PendingAttachment[]>([]);
  readonly pendingAttachments$ = this.pendingAttachments$$.asObservable();

  // Track if user has ever uploaded files in this session
  private readonly hasEverUploaded$$ = new BehaviorSubject<boolean>(false);
  readonly hasEverUploaded$ = this.hasEverUploaded$$.asObservable();

  /**
   * Get current pending attachments value
   */
  get pendingAttachments(): PendingAttachment[] {
    return this.pendingAttachments$$.value;
  }

  /**
   * Get completed attachments (ready to be sent with message)
   */
  get completedAttachments(): PendingAttachment[] {
    return this.pendingAttachments$$.value.filter(
      (a) => a.uploadState === 'completed' && a.resourceId,
    );
  }

  /**
   * Get resource IDs of completed attachments
   */
  get completedResourceIds(): string[] {
    return this.completedAttachments
      .map((a) => a.resourceId)
      .filter((id): id is string => !!id);
  }

  /**
   * Upload files to a session
   * Creates pending attachments and starts upload
   */
  uploadFiles(sessionId: string, files: File[]): void {
    if (!sessionId || files.length === 0) {
      return;
    }

    // Mark that user has uploaded at least once
    this.hasEverUploaded$$.next(true);

    // Create pending attachments for each file
    const newAttachments: PendingAttachment[] = files.map((file) => ({
      localId: `local-${++localIdCounter}`,
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
      previewUrl: this.createPreviewUrl(file),
      uploadProgress: 0,
      uploadState: 'uploading' as const,
    }));

    // Add to pending attachments
    this.pendingAttachments$$.next([
      ...this.pendingAttachments$$.value,
      ...newAttachments,
    ]);

    // Start upload
    this.filesApiService
      .uploadFiles(sessionId, files)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (event: UploadEvent) => {
          if (event.type === 'progress') {
            // Update progress for all new attachments
            this.updateAttachmentsProgress(
              newAttachments.map((a) => a.localId),
              event.progress,
            );
            // When upload reaches 100%, transition to processing state
            // Server is now optimizing/rotating/compressing the images
            if (event.progress === 100) {
              this.markAttachmentsProcessing(newAttachments.map((a) => a.localId));
            }
          } else if (event.type === 'complete') {
            // Map uploaded resources to pending attachments
            this.completeAttachmentUpload(newAttachments, event.resources);
            // Refetch session to update checklist with new resources
            this.refetchSession(sessionId);
            // Track successful upload
            this.amplitudeService.trackEvent('grading_files_uploaded', {
              fileCount: files.length,
              totalSize: files.reduce((sum, f) => sum + f.size, 0),
              mimeTypes: [...new Set(files.map((f) => f.type))],
              method: 'file_picker',
            });
          }
        },
        error: (err) => {
          console.error('Failed to upload files:', err);
          // Mark all new attachments as error
          this.markAttachmentsError(
            newAttachments.map((a) => a.localId),
            'Upload failed. Please try again.',
          );
          // Track failed upload
          this.amplitudeService.trackEvent('grading_file_upload_failed', {
            fileCount: files.length,
            errorMessage: err?.message || 'Unknown error',
          });
        },
      });
  }

  /**
   * Remove a pending attachment
   */
  removePendingAttachment(sessionId: string | null, localId: string): void {
    const attachments = this.pendingAttachments$$.value;
    const attachment = attachments.find((a) => a.localId === localId);

    if (!attachment) {
      return;
    }

    // Revoke blob URL if exists
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }

    // Remove from pending attachments
    this.pendingAttachments$$.next(
      attachments.filter((a) => a.localId !== localId),
    );

    // If already uploaded, remove from backend
    if (attachment.resourceId && attachment.uploadState === 'completed' && sessionId) {
      this.filesApiService
        .removeFile(sessionId, attachment.resourceId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (err) => {
            console.error('Failed to remove file from backend:', err);
          },
        });
    }
  }

  /**
   * Clear all pending attachments
   */
  clearPendingAttachments(): void {
    const attachments = this.pendingAttachments$$.value;

    // Revoke all blob URLs
    for (const attachment of attachments) {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    this.pendingAttachments$$.next([]);
  }

  /**
   * Load example files from public folder and upload them
   * Used for demo/testing when user doesn't have their own files
   */
  async loadExampleFiles(sessionId: string): Promise<void> {
    if (!sessionId) {
      return;
    }

    // Mark that user has used examples (same as uploading)
    this.hasEverUploaded$$.next(true);

    try {
      // Fetch example files from public folder
      const files: File[] = [];
      for (const path of EXAMPLE_FILES) {
        const response = await fetch(path);
        if (!response.ok) {
          console.error(`Failed to fetch example file: ${path}`);
          continue;
        }
        const blob = await response.blob();
        const fileName = path.split('/').pop() || 'example.png';
        const file = new File([blob], fileName, { type: blob.type || 'image/png' });
        files.push(file);
      }

      if (files.length > 0) {
        this.uploadFiles(sessionId, files);
        this.amplitudeService.trackEvent('grading_example_files_loaded', {
          fileCount: files.length,
        });
      }
    } catch (error) {
      console.error('Failed to load example files:', error);
    }
  }

  /**
   * Clear only completed attachments (after sending a message)
   * Returns the cleared attachments for message creation
   */
  clearCompletedAttachments(): PendingAttachment[] {
    const completed = this.completedAttachments;

    // Keep only non-completed attachments
    this.pendingAttachments$$.next(
      this.pendingAttachments$$.value.filter(
        (a) => a.uploadState !== 'completed',
      ),
    );

    // Revoke blob URLs for cleared attachments
    for (const attachment of completed) {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    return completed;
  }

  // ============================================
  // Private Helpers
  // ============================================

  /**
   * Create a blob URL for file preview
   */
  private createPreviewUrl(file: File): string | undefined {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return undefined;
  }

  /**
   * Refetch session to update resources for checklist
   */
  private refetchSession(sessionId: string): void {
    this.gradingApiService
      .getGrading(sessionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          const currentSession = this.stateService.session;
          if (currentSession) {
            this.stateService.setSession({
              ...session,
              messages: currentSession.messages,
            });
          }
        },
        error: (err) => {
          console.error('Failed to refetch session after upload:', err);
        },
      });
  }

  /**
   * Update progress for multiple attachments
   */
  private updateAttachmentsProgress(localIds: string[], progress: number): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      if (localIds.includes(a.localId)) {
        return { ...a, uploadProgress: progress };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);
  }

  /**
   * Complete upload for attachments
   * If resources are still processing (AI rotation detection), start watching via SSE
   */
  private completeAttachmentUpload(
    newAttachments: PendingAttachment[],
    resources: Resource[],
  ): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      const index = newAttachments.findIndex((na) => na.localId === a.localId);
      if (index !== -1 && resources[index]) {
        const resource = resources[index];
        const needsProcessing =
          resource.processingStatus === 'pending' ||
          resource.processingStatus === 'processing';

        return {
          ...a,
          resourceId: resource.id,
          signedUrl: resource.signedUrl,
          uploadProgress: 100,
          // Stay in 'processing' state if backend is still processing (AI rotation)
          uploadState: needsProcessing ? ('processing' as const) : ('completed' as const),
        };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);

    // Start watching resources that need processing
    for (let i = 0; i < newAttachments.length; i++) {
      const resource = resources[i];
      if (
        resource &&
        (resource.processingStatus === 'pending' ||
          resource.processingStatus === 'processing')
      ) {
        this.watchResourceProcessing(newAttachments[i].localId, resource.id);
      }
    }
  }

  /**
   * Watch a resource's processing status via SSE
   * Updates attachment when processing completes
   */
  private watchResourceProcessing(localId: string, resourceId: string): void {
    this.filesApiService
      .watchResourceStatus(resourceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resource) => {
          // Update attachment with latest resource data
          if (
            resource.processingStatus === 'completed' ||
            resource.processingStatus === 'failed'
          ) {
            this.updateAttachmentFromResource(localId, resource);
          }
        },
        error: (err) => {
          console.error(`Failed to watch resource ${resourceId}:`, err);
          // On error, mark as completed anyway (resource might still be usable)
          this.markAttachmentCompleted(localId);
        },
      });
  }

  /**
   * Update attachment from processed resource
   */
  private updateAttachmentFromResource(localId: string, resource: Resource): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      if (a.localId === localId) {
        return {
          ...a,
          signedUrl: resource.signedUrl, // Use the new signed URL (processed/rotated image)
          uploadState:
            resource.processingStatus === 'failed'
              ? ('error' as const)
              : ('completed' as const),
          errorMessage:
            resource.processingStatus === 'failed'
              ? 'Processing failed'
              : undefined,
        };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);
  }

  /**
   * Mark attachment as completed (fallback)
   */
  private markAttachmentCompleted(localId: string): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      if (a.localId === localId && a.uploadState === 'processing') {
        return { ...a, uploadState: 'completed' as const };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);
  }

  /**
   * Mark attachments as processing (upload done, server optimizing)
   */
  private markAttachmentsProcessing(localIds: string[]): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      if (localIds.includes(a.localId) && a.uploadState === 'uploading') {
        return { ...a, uploadState: 'processing' as const };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);
  }

  /**
   * Mark attachments as error
   */
  private markAttachmentsError(localIds: string[], errorMessage: string): void {
    const attachments = this.pendingAttachments$$.value.map((a) => {
      if (localIds.includes(a.localId)) {
        return { ...a, uploadState: 'error' as const, errorMessage };
      }
      return a;
    });
    this.pendingAttachments$$.next(attachments);
  }
}
